import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SKELETON } from '@progress/kendo-angular-indicators';
import { KENDO_CARD, KENDO_LAYOUT, StepperActivateEvent } from '@progress/kendo-angular-layout';
import { IconService } from '@unity/services/icon.service';
import { FormStep } from '@unity/utils/types';
import { SurveyModule } from 'survey-angular-ui';
import { SurveyModel } from 'survey-core';

@Component({
  selector: 'unity-stepper-survey-shared',
  imports: [KENDO_LAYOUT, KENDO_SKELETON, KENDO_BUTTON, KENDO_CARD, SurveyModule, ReactiveFormsModule],
  templateUrl: './stepper-survey-shared.component.html',
  styleUrl: './stepper-survey-shared.component.scss',
})
export class StepperSurveySharedComponent {
  private readonly iconService = inject(IconService);

  protected arrowRightIcon = this.iconService.arrowRightIcon;
  protected arrowLeftIcon = this.iconService.arrowLeftIcon;
  protected cancelIcon = this.iconService.cancelIcon;
  protected saveIcon = this.iconService.saveIcon;

  /* Inputs */
  public readonly $surveyModel = input.required<SurveyModel>({ alias: 'surveyModel' });
  public readonly $isLoading = input.required<boolean>({ alias: 'isLoading' });

  /* Outputs */
  public readonly pageChangeEvent = output<number>();

  /* Stepper */
  protected readonly $currentPage = signal<number>(1);

  protected readonly $formPageCount = computed(() => {
    const model = this.$surveyModel();
    return model instanceof SurveyModel ? model.PageCount : 1;
  });

  protected readonly $pageIndex = computed(() => this.$currentPage() - 1);

  protected readonly $showStepper = computed(() => {
    const pageCount = this.$formPageCount();
    return pageCount && pageCount > 1;
  });

  protected readonly $showPrevious = computed(() => {
    const currentPage = this.$currentPage();
    const showStepper = this.$showStepper();
    return showStepper && currentPage > 1;
  });

  protected readonly $showNext = computed(() => {
    const pageCount = this.$formPageCount();
    const currentPage = this.$currentPage();
    const showStepper = this.$showStepper();
    return pageCount && showStepper && currentPage < pageCount;
  });

  protected readonly $showSubmit = computed(() => {
    const pageCount = this.$formPageCount();
    if (pageCount === this.$currentPage()) {
      return true;
    } else {
      return false;
    }
  });

  protected readonly $formSteps = computed(() => {
    const steps = [];
    const pageCount = this.$formPageCount();
    if (pageCount) {
      for (let i = 1; i <= pageCount; i++) {
        const step: FormStep = { label: 'Page ' + i, isValid: true, page: i };
        steps.push(step);
      }
    }
    return steps;
  });

  public constructor() {
    effect(() => {
      const currentPage = this.$currentPage();
      this.pageChangeEvent.emit(currentPage);
    });
  }

  protected nextPage() {
    const model = this.$surveyModel();
    const validated = this.validatePage();
    if (validated && model instanceof SurveyModel) {
      this.$currentPage.update(currentPage => currentPage + 1);
      model.nextPage();
    }
  }
  protected prevPage() {
    const model = this.$surveyModel();
    this.$currentPage.update(currentPage => currentPage - 1);
    model.prevPage();
  }

  protected navigateToPage(pageIndex: number) {
    const model = this.$surveyModel();
    const currentPage = this.$currentPage();
    const pageNo = pageIndex + 1;
    const validated = this.validatePage();
    if (pageNo < currentPage || (pageNo > currentPage && validated)) {
      this.$currentPage.set(pageNo);
      const page = model instanceof SurveyModel && model?.getPage(pageIndex);
      return page && model?.tryNavigateToPage(page);
    }
    return this.$currentPage.set(currentPage);
  }

  protected activateStep(e: StepperActivateEvent) {
    const currentPage = this.$currentPage();

    const pageNo = e.index + 1;
    if (pageNo < currentPage) {
      return;
    }

    const validated = this.validatePage();
    if (!validated) {
      return e.preventDefault();
    }
  }

  protected validatePage() {
    const model = this.$surveyModel();
    const validated = model.validatePage();
    return validated;
  }
}
